/**
 * `world_clock` → **Customize World clock column** — rebuilt 1:1 from the capture
 * (`discovery/ui-reference/editors/world_clock/dialog-settings.{png,json}`).
 *
 * Measured: a 320 × 354 card; the title *World Clock column settings*; **Format** over a
 * 288 × 32 group *12H* | *24H*; **UTC Offset** over a second group *Show* | *Hide*;
 * **Set beginning of workday hours** (199 × 21) and **Set end of workday hours** (160 × 21),
 * each over its own control.
 *
 * The two workday controls' *widgets* are outside the capture's text tree — their labels are
 * recorded, the pickers under them are not — and `worldClockSettingsSchema` stores each as a
 * **string** `"0"`–`"23"`, so they are rendered as an hour `<select>` of the 24 values the store
 * accepts. The widget shape is q520; the stored values are monday's own.
 *
 * `format` is `"HH:mm"` | `"hh:mm A"` in `settings_str` `[09§A.7 world_clock]` — *not* the
 * `12H`/`24H` spelling `hour` uses — so the buttons carry monday's labels and write monday's
 * values.
 */
import type { WorldClockSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SegmentedField,
  SettingsSelect,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const FORMATS = [
  { id: '12H', label: '12H', value: 'hh:mm A' },
  { id: '24H', label: '24H', value: 'HH:mm' },
] as const;

const UTC_OFFSET = [
  { id: 'show', label: 'Show', value: true },
  { id: 'hide', label: 'Hide', value: false },
] as const;

/** `"0"` … `"23"`, the strings monday stores (`startWorkingHours` / `endWorkingHours`). */
const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${String(hour).padStart(2, '0')}:00`,
}));

export function WorldClockSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<WorldClockSettings>) {
  const [draft, patch] = useSettingsDraft<WorldClockSettings>(settings);
  return (
    <SettingsForm
      title="World Clock column settings"
      testId="world-clock-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Format"
        testId="world-clock-format"
        value={draft.format ?? 'hh:mm A'}
        options={FORMATS}
        onChange={(next) => patch({ format: next })}
      />
      <SegmentedField
        label="UTC Offset"
        testId="world-clock-utc-offset"
        value={draft.show_utc_offset ?? false}
        options={UTC_OFFSET}
        onChange={(next) => patch({ show_utc_offset: next })}
      />
      <SettingsSelect
        label="Set beginning of workday hours"
        testId="world-clock-start"
        value={draft.startWorkingHours ?? '9'}
        options={HOURS}
        onChange={(next) => patch({ startWorkingHours: next })}
      />
      <SettingsSelect
        label="Set end of workday hours"
        testId="world-clock-end"
        value={draft.endWorkingHours ?? '17'}
        options={HOURS}
        onChange={(next) => patch({ endWorkingHours: next })}
      />
      <HideSummaryField
        testId="world-clock-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
